import React, { forwardRef, useCallback, useEffect, useMemo, useReducer } from 'react';

import EventRoundedIcon from '@material-ui/icons/EventRounded';
import { addMonths, startOfDay, subMonths } from 'date-fns';
import DateRangeSelectionDropdown from 'screens/analytics/DateRangeSelectionDropdown';

import {
  TEAMHUB_LAST_ACTIVITY_PREDEFINED_DATERANGES,
  TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP,
} from 'constants/ProjectConstants';
import { convertDateToString2 } from 'constants/helper';
import { sendAppliedFilterEvent } from 'constants/mixpanelHelper';

import FilterDisplay from './FilterDisplay';
import { buildDateFilters, getSelectedFiltersCountFromMap } from './helper';

const LAST_ACTIVITY_DATE_FILTER_UPDATE = 'LAST_ACTIVITY_DATE_FILTER_UPDATE';
const LAST_ACTIVITY_DATE_FILTER_UPDATE_FROM = 'LAST_ACTIVITY_DATE_FILTER_UPDATE_FROM';
const LAST_ACTIVITY_DATE_FILTER_UPDATE_TO = 'LAST_ACTIVITY_DATE_FILTER_UPDATE_TO';
const LAST_ACTIVITY_DATE_FILTER_UPDATE_PREDEFINED = 'LAST_ACTIVITY_DATE_FILTER_UPDATE_PREDEFINED';
const LAST_ACTIVITY_CLEAR = 'LAST_ACTIVITY_CLEAR';

export const initialLastActivityDateFilterState = { from: null, to: null, predefined: null };
export function lastActivityDateFilterReducer(state = initialLastActivityDateFilterState, action) {
  switch (action.type) {
    case LAST_ACTIVITY_DATE_FILTER_UPDATE_FROM: {
      return { ...state, predefined: null, from: action.date };
    }
    case LAST_ACTIVITY_DATE_FILTER_UPDATE_TO: {
      return { ...state, predefined: null, to: action.date };
    }
    case LAST_ACTIVITY_DATE_FILTER_UPDATE_PREDEFINED: {
      return { ...initialLastActivityDateFilterState, predefined: action.key };
    }
    case LAST_ACTIVITY_DATE_FILTER_UPDATE: {
      return { ...action.filters };
    }
    case LAST_ACTIVITY_CLEAR: {
      return { ...initialLastActivityDateFilterState };
    }
    default:
      return state;
  }
}

const TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP_REVERSED = Object.entries(
  TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP
).reduce(
  (prev, [k, v]) => ({
    ...prev,
    [v]: k,
  }),
  {}
);

export const buildLastActivityDateFilters = buildDateFilters('LAST_ACTIVITY_DATE');

const getFilterTooltip = (selectedFilters) => {
  const { from, to, predefined } = selectedFilters;

  if (predefined) {
    return TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP_REVERSED[predefined];
  }

  if (from && to) return `${convertDateToString2(from)} - ${convertDateToString2(to)}`;

  return '';
};

export default function LastActivityDateFilter({
  selectedFiltersDispatch,
  selectedFilters = initialLastActivityDateFilterState,
  width = '20%',
  selectedTaskType,
}) {
  const [state, currentFiltersDispatch] = useReducer(
    lastActivityDateFilterReducer,
    selectedFilters || initialLastActivityDateFilterState
  );

  const onApply = useCallback(() => {
    selectedFiltersDispatch({ type: LAST_ACTIVITY_DATE_FILTER_UPDATE, filters: state });
  }, [state]);

  const onClear = (e) => {
    e.stopPropagation();
    selectedFiltersDispatch({ type: LAST_ACTIVITY_CLEAR });
    document.body.click();
  };

  const selectedCount = Math.min(1, getSelectedFiltersCountFromMap(selectedFilters));

  const widgetSupportedStructure = useMemo(() => {
    if (state.predefined) {
      const matchedValue = TEAMHUB_LAST_ACTIVITY_PREDEFINED_DATERANGES.find(
        (range) => range.label === TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP_REVERSED[state.predefined]
      );

      return {
        label: matchedValue?.label,
        startDate: matchedValue?.startDate,
        endDate: matchedValue?.endDate,
      };
    }

    if (state.from && state.to) {
      return {
        startDate: state.from,
        endDate: state.to,
        label: '',
      };
    }

    return {};
  }, [state]);

  const updateDateRange = ({ startDate, endDate, label = '' }) => {
    if (label) {
      currentFiltersDispatch({
        type: LAST_ACTIVITY_DATE_FILTER_UPDATE,
        filters: {
          from: null,
          to: null,
          predefined: TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP[label],
        },
      });
    } else {
      currentFiltersDispatch({
        type: LAST_ACTIVITY_DATE_FILTER_UPDATE,
        filters: {
          from: startDate,
          to: endDate,
          predefined: TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP[label],
        },
      });
    }
  };

  useEffect(() => {
    currentFiltersDispatch({ type: LAST_ACTIVITY_DATE_FILTER_UPDATE, filters: selectedFilters });
  }, [JSON.stringify(selectedFilters)]);

  useEffect(() => {
    onApply();
  }, [state]);

  return (
    <DateRangeSelectionDropdown
      DisplayComponent={forwardRef(({ onClick }, ref) => (
        <FilterDisplay
          ref={ref}
          width={width}
          icon={<EventRoundedIcon />}
          name="Last Activity"
          selectedCount={selectedCount}
          filterTooltip={getFilterTooltip(selectedFilters)}
          onClear={onClear}
          onClick={() => {
            onClick();
            sendAppliedFilterEvent({
              eventProperties: {
                filter: 'Last Activity Date',
                source: selectedTaskType,
              },
            });
          }}
        />
      ))}
      dateRange={widgetSupportedStructure}
      onDateRangeChange={updateDateRange}
      predefinedRanges={TEAMHUB_LAST_ACTIVITY_PREDEFINED_DATERANGES}
      minDate={subMonths(startOfDay(new Date()), 11)}
      maxDate={addMonths(startOfDay(new Date()), 11)}
      zIndex={10}
      disablePortal={false}
      modifiers={{
        flip: {
          enabled: false,
        },
      }}
      clearable
    />
  );
}
